/**
 * HiggsfieldCommands — copy-paste-ready @higgsfield/cli commands built
 * from a character's stored fields (issue #589, "Option C").
 *
 * Higgsfield generation stays out-of-band (the dashboard never calls
 * the CLI — see marketing/README.md for the trust-boundary rationale).
 * What this panel removes is the *friction*: instead of hand-writing
 * prompts and remembering flags, the admin copies the exact commands —
 * with the persona's description + traits already folded into the
 * prompt — runs them in a terminal, reviews the output, and uploads.
 *
 * The command set mirrors the verified CLI surface:
 *   generate create nano_banana_2 [--image <anchor>]   → stills
 *   upload create <file>                               → upload id
 *   soul-id create --soul-2 --image …                  → Soul ref id
 *   generate create text2image_soul_v2 --custom_reference_id <soul>
 *   generate create seedance1_5 --image <still>        → UGC clip
 */

import { useState } from 'react';
import type { Character } from '../lib/types';

// Strip the descriptive suffix admins tend to add to names
// ("Maya Okafor — solo backpacker, 24") down to a clean Soul label.
function soulName(name: string): string {
  return name.split(/[—–-]/)[0].trim() || name.trim();
}

// Collapse description + traits into a single-line image prompt and make
// it safe to drop inside a double-quoted shell argument.
function shellArg(s: string): string {
  return s.replace(/\s+/g, ' ').replace(/"/g, '\\"').trim();
}

function buildImagePrompt(c: Character): string {
  const traits = Object.entries(c.traits ?? {})
    .filter(([k]) => k.trim().length > 0)
    .map(([k, v]) => `${k}: ${v}`)
    .join(', ');
  const parts = [
    'Photorealistic UGC-style portrait.',
    c.description,
    traits ? `Traits — ${traits}.` : '',
    'Natural light, authentic phone-camera look, head and shoulders, looking at camera.',
  ].filter(Boolean);
  return shellArg(parts.join(' '));
}

function buildVideoPrompt(c: Character): string {
  const who = soulName(c.name);
  return shellArg(
    `${who} talks to camera, handheld selfie style, authentic UGC reel, natural light, speaking naturally`,
  );
}

export function HiggsfieldCommands({ character }: { character: Character }) {
  const prompt = buildImagePrompt(character);
  const vprompt = buildVideoPrompt(character);
  const name = soulName(character.name);
  const soul = character.higgsfield_character_id?.trim() || null;

  const heroCmd =
    `higgsfield generate create nano_banana_2 --aspect_ratio 4:5 --resolution 2k --json \\\n` +
    `  --prompt "${prompt}"`;

  const varCmd =
    `# upload a chosen still first → use its id as the consistency anchor\n` +
    `higgsfield upload create ./still.png\n` +
    `higgsfield generate create nano_banana_2 --aspect_ratio 4:5 --resolution 2k --json \\\n` +
    `  --image <UPLOAD_ID> --prompt "${prompt} Three-quarter angle, candid."`;

  const soulCmd =
    `# train a Soul ref from 5–20 uploaded stills, then copy the soul id into the field above\n` +
    `higgsfield soul-id create --name "${name}" --soul-2 \\\n` +
    `  --image <ID1> --image <ID2> --image <ID3> --image <ID4> --image <ID5>\n` +
    `higgsfield soul-id wait <SOUL_ID>`;

  const soulImageCmd = soul
    ? `# on-model stills using this character's trained Soul\n` +
      `higgsfield generate create text2image_soul_v2 --aspect_ratio 9:16 --quality 2k --json \\\n` +
      `  --custom_reference_id ${soul} --prompt "${prompt}"`
    : null;

  const videoCmd =
    `# turn a still into a UGC clip (then upload it on the Assets tab, source = higgsfield)\n` +
    `# NOTE: --aspect_ratio 9:16 is required — seedance defaults to 16:9 (landscape)\n` +
    `higgsfield generate create seedance1_5 --aspect_ratio 9:16 --duration 8 --resolution 720p --json \\\n` +
    `  --image <STILL_OR_UPLOAD_ID> --prompt "${vprompt}"`;

  return (
    <details
      style={{
        background: 'var(--color-surface-alt)',
        borderRadius: 10,
        padding: '12px 14px',
        marginTop: 16,
      }}
    >
      <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
        Higgsfield commands
      </summary>
      <p style={{ fontSize: 12, color: 'var(--color-text-muted)', margin: '8px 0 12px' }}>
        Built from this character's description + traits. Run them in a terminal with{' '}
        <code>@higgsfield/cli</code> ({soul ? 'Soul trained' : 'no Soul yet'}), review the output,
        then paste the Soul id into the field above and upload your best still as the reference
        image.
      </p>

      <CommandBlock label="1 · Generate a hero still" command={heroCmd} />
      <CommandBlock label="2 · Variations off a chosen still (consistency)" command={varCmd} />
      <CommandBlock label="3 · Train the Soul ref" command={soulCmd} />
      {soulImageCmd && (
        <CommandBlock label="4 · On-model stills from the trained Soul" command={soulImageCmd} />
      )}
      <CommandBlock label={`${soulImageCmd ? '5' : '4'} · Generate a UGC clip`} command={videoCmd} />
    </details>
  );
}

function CommandBlock({ label, command }: { label: string; command: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (insecure context / permissions) — the text is
      // still selectable in the block below.
    }
  }

  return (
    <div style={{ marginBottom: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 4,
        }}
      >
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)' }}>
          {label}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
      <pre
        style={{
          margin: 0,
          padding: '10px 12px',
          background: 'var(--color-bg, #0d0d0d)',
          borderRadius: 8,
          overflowX: 'auto',
          fontSize: 12,
          lineHeight: 1.5,
          whiteSpace: 'pre',
        }}
      >
        <code>{command}</code>
      </pre>
    </div>
  );
}
